/**
 * DemographicBreakdownAccordion.jsx - Collapsible demographic view component
 * Displays demographic response patterns in a clean, expandable format
 * Extracted from Analysis.jsx
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { humanizeColumnName } from '../../lib/formatters';

const DemographicBreakdownAccordion = ({ 
  allBreakdowns, 
  orderedResponseScale, 
  scaleColors,
  scaleColorMap,
  aggregation 
}) => {
  const [expandedDemos, setExpandedDemos] = useState(new Set());
  const [showAll, setShowAll] = useState(false);
  
  if (!allBreakdowns || Object.keys(allBreakdowns).length === 0) return null;
  
  // Calculate divergence for each demographic to rank them by importance
  const demoScores = Object.entries(allBreakdowns).map(([demoName, demoData]) => {
    if (!demoData?.breakdown) return { name: demoName, score: 0, data: demoData };
    const globalPcts = aggregation?.percentages || {};
    const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(globalPcts);
    let maxDivergence = 0;
    for (const [group, data] of Object.entries(demoData.breakdown)) {
      if (!data?.percentages) continue;
      let divergence = 0;
      for (const k of scale) divergence += Math.abs((data.percentages[k] || 0) - (globalPcts[k] || 0));
      divergence /= 2;
      if (divergence > maxDivergence) maxDivergence = divergence;
    }
    return { name: demoName, score: maxDivergence, data: demoData };
  }).sort((a, b) => b.score - a.score);
  
  // Show top 4 by default, or all if showAll
  const visibleDemos = showAll ? demoScores : demoScores.slice(0, 4);
  const hiddenCount = demoScores.length - 4;
  
  const toggleExpand = (demoName) => {
    setExpandedDemos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(demoName)) newSet.delete(demoName);
      else newSet.add(demoName);
      return newSet;
    });
  };
  
  return (
    <Card className="border border-border" data-testid="per-demographic-comparison">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Demographic Response Patterns</CardTitle>
            <p className="text-xs text-muted-foreground">
              Click to expand each attribute and see detailed group breakdowns
            </p>
          </div>
          <Badge variant="outline" className="text-xs">{demoScores.length} attributes</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {visibleDemos.map(({ name: demoName, score, data: demoData }) => {
          if (!demoData?.breakdown || Object.keys(demoData.breakdown).length < 2) return null;
          const isExpanded = expandedDemos.has(demoName);
          const groups = Object.entries(demoData.breakdown);
          const groupCount = groups.length;
          
          // Calculate quick summary stats
          const topGroup = groups.reduce((best, [gName, gData]) => {
            if (!gData?.percentages) return best;
            const sorted = Object.entries(gData.percentages).sort((a, b) => b[1] - a[1]);
            const topPct = sorted[0]?.[1] || 0;
            return topPct > (best.pct || 0) ? { name: gName, response: sorted[0]?.[0], pct: topPct } : best;
          }, {});
          
          return (
            <div key={demoName} className="border border-slate-100 rounded-lg overflow-hidden">
              {/* Collapsible Header */}
              <button
                onClick={() => toggleExpand(demoName)}
                className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors text-left"
                data-testid={`demo-accordion-${demoName}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-6 h-6 rounded flex items-center justify-center text-xs font-bold",
                    score > 12 ? "bg-negative-soft text-negative-soft-foreground" : score > 7 ? "bg-warning-soft text-warning-soft-foreground" : "bg-blue-100 text-blue-700"
                  )}>
                    {score.toFixed(0)}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-800">{humanizeColumnName(demoName)}</span>
                    <span className="text-xs text-muted-foreground ml-2">({groupCount} groups)</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!isExpanded && topGroup.name && (
                    <span className="text-xs text-slate-500 hidden sm:inline">
                      Peak: <span className="font-medium">{topGroup.name}</span> → {topGroup.pct?.toFixed(0)}% {topGroup.response}
                    </span>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>
              
              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 bg-slate-50/50 border-t border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {groups.slice(0, 12).map(([group, data]) => {
                      if (!data?.percentages) return null;
                      const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(data.percentages);
                      const totalResp = Object.values(data.distribution || {}).reduce((s, v) => s + v, 0);
                      const sorted = Object.entries(data.percentages).sort((a, b) => b[1] - a[1]);
                      const topResponse = sorted[0];
                      
                      return (
                        <div key={group} className="bg-white border border-slate-100 rounded-md p-2 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-700 truncate max-w-[140px]" title={group}>
                              {group}
                            </span>
                            <span className="text-[10px] text-muted-foreground">n={totalResp}</span>
                          </div>
                          <div className="flex h-3 rounded overflow-hidden">
                            {scale.map((resp) => {
                              const pct = data.percentages[resp] || 0;
                              return pct > 0 ? (
                                <div 
                                  key={resp} 
                                  className="transition-all duration-300 relative group/bar" 
                                  style={{ 
                                    width: `${pct}%`, 
                                    backgroundColor: scaleColorMap?.[resp] || scaleColors[orderedResponseScale.indexOf(resp)] || '#94A3B8', 
                                    minWidth: pct > 1 ? '2px' : '0' 
                                  }}
                                >
                                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover/bar:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                    {resp}: {pct.toFixed(0)}%
                                  </div>
                                </div>
                              ) : null;
                            })}
                          </div>
                          {topResponse && (
                            <p className="text-[10px] text-slate-500 truncate">
                              Top: <span className="font-medium text-slate-700">{topResponse[0]}</span> ({topResponse[1].toFixed(0)}%)
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {groups.length > 12 && (
                      <div className="flex items-center justify-center text-xs text-muted-foreground p-2">
                        +{groups.length - 12} more groups
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Show More / Less Button */}
        {hiddenCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="w-full text-xs text-muted-foreground hover:text-slate-700"
          >
            {showAll ? (
              <>Show Less <ChevronUp className="w-3 h-3 ml-1" /></>
            ) : (
              <>Show {hiddenCount} More Attributes <ChevronDown className="w-3 h-3 ml-1" /></>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default DemographicBreakdownAccordion;
